import React from "react";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import Container from "./Container";
import Button from "./Button";
import { CTA_URL, IMAGES, LANDING_COPY } from "../constants";

const ProgramSections: React.FC = () => {
  const { whatIs, forWho, different, outcomes, safety, process } = LANDING_COPY;

  return (
    <>
      <section id="que-es" className="bg-white py-20 md:py-24">
        <Container>
          <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h2 className="animate-fade-in-up font-display text-3xl font-semibold text-zinc-900 md:text-5xl">
                {whatIs.title}
              </h2>
              <div className="animate-fade-in-up delay-100 mt-7 space-y-5 text-lg leading-relaxed text-zinc-700">
                {whatIs.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <img
              src={IMAGES.section}
              alt="Experiencia de aventura en Europa con AsiViajo"
              className="animate-soft-scale h-full min-h-80 w-full rounded-2xl border border-zinc-300 object-cover"
            />
          </div>
        </Container>
      </section>

      <section id="para-quien" className="bg-zinc-100 py-20 md:py-24">
        <Container>
          <div className="max-w-4xl">
            <h2 className="animate-fade-in-up font-display text-3xl font-semibold text-zinc-900 md:text-5xl">
              {forWho.title}
            </h2>
            <p className="animate-fade-in-up delay-100 mt-6 text-lg text-zinc-700">
              {forWho.intro}
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {forWho.bullets.map((bullet, index) => (
              <article
                key={bullet}
                className="animate-fade-in-up rounded-xl border border-zinc-300 bg-white px-6 py-5 transition hover:-translate-y-0.5"
                style={{ animationDelay: `${80 + index * 70}ms` }}
              >
                <p className="leading-relaxed text-zinc-800">{bullet}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section id="diferente" className="bg-white py-20 md:py-24">
        <Container>
          <h2 className="animate-fade-in-up font-display text-3xl font-semibold text-zinc-900 md:text-5xl">
            {different.title}
          </h2>

          <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {different.items.map((item, index) => (
              <article
                key={item.title}
                className="animate-fade-in-up group rounded-xl border border-zinc-300 bg-zinc-50 px-6 py-6"
                style={{ animationDelay: `${100 + index * 70}ms` }}
              >
                <span className="mb-3 inline-flex h-8 w-8 items-center justify-center border border-brand-pink/50 bg-brand-pink-soft text-xs font-semibold text-brand-pink-dark">
                  {index + 1}
                </span>
                <h3 className="text-lg font-semibold text-zinc-900">
                  {item.title}
                </h3>
                <p className="mt-3 leading-relaxed text-zinc-700">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 py-20 text-white md:py-24">
        <Container>
          <h2 className="animate-fade-in-up font-display text-3xl font-semibold md:text-5xl">
            {outcomes.title}
          </h2>
          <p className="animate-fade-in-up delay-100 mt-6 text-lg text-zinc-200">
            {outcomes.intro}
          </p>
          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {outcomes.bullets.map((bullet, index) => (
              <li
                key={bullet}
                className="animate-fade-in-up flex items-start gap-3 rounded-xl border border-white/20 bg-white/5 p-5"
                style={{ animationDelay: `${120 + index * 80}ms` }}
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-brand-pink" />
                <span className="text-zinc-100">{bullet}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-sm uppercase tracking-[0.1em] text-zinc-300">
            {outcomes.note}
          </p>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-24">
        <Container>
          <div className="animate-soft-scale rounded-2xl border border-zinc-300 bg-zinc-100 p-8 md:p-12">
            <div className="flex items-start gap-4">
              <ShieldCheck className="mt-1 h-7 w-7 text-brand-pink" />
              <div>
                <h2 className="font-display text-3xl font-semibold text-zinc-900 md:text-4xl">
                  {safety.title}
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-zinc-700">
                  {safety.text}
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section id="como-funciona" className="bg-zinc-100 py-20 md:py-24">
        <Container>
          <div className="max-w-4xl">
            <h2 className="animate-fade-in-up font-display text-3xl font-semibold text-zinc-900 md:text-5xl">
              {process.title}
            </h2>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {process.steps.map((step, index) => (
              <article
                key={step}
                className="animate-fade-in-up rounded-xl border border-zinc-300 bg-white px-6 py-5"
                style={{ animationDelay: `${100 + index * 90}ms` }}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-pink-dark">
                  Paso {index + 1}
                </p>
                <p className="mt-2 text-lg text-zinc-800">{step}</p>
              </article>
            ))}
          </div>

          <div className="animate-fade-in-up delay-250 mt-10">
            <Button href={CTA_URL}>{process.cta}</Button>
          </div>
        </Container>
      </section>
    </>
  );
};

export default ProgramSections;
